'use client';

import { useTranslations } from 'next-intl';
import { useNextStepLabel } from 'components/ring-air/hooks/useNextStepLabel';
import { analytics } from 'lib/analytics/analytics';
import { ShippingBox } from 'assets/icons';
import {
  MidPanel,
  MidPanelPhone,
  LeftPanel,
  RightPanel,
} from 'components/shared/BottomBar';
import { useContext } from 'react';
import { getEngravingKey } from 'content/products/ring/productMap/constants';
import { RingProVariants } from 'content/products/ring-pro';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  CustomImage as Image,
  DotSpinner,
  useModal,
} from 'components/shared';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { RingProSelectionContext } from './RingProSelectionContext';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { ApplePayExpressButton } from 'components/shared/ApplePayExpressButton';
import { hasMaxSelected } from 'content/products/max';
import { useRetouchFlowContext } from 'components/shared/RetouchFlow';
import styles from './BottomBar.module.css';

const cx = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(' ');

const PAYMENT_ICON_BASE = 'web_v2/icons/payment-method-circle';
const DEFAULT_PAYMENT_ICONS = [
  `${PAYMENT_ICON_BASE}/visa.png`,
  `${PAYMENT_ICON_BASE}/mastercard.png`,
  `${PAYMENT_ICON_BASE}/apple-pay.png`,
];
const PAYMENT_ICONS_BY_STORE: Record<string, string[]> = {
  IN: [
    `${PAYMENT_ICON_BASE}/visa.png`,
    `${PAYMENT_ICON_BASE}/mastercard.png`,
    `${PAYMENT_ICON_BASE}/gpay.png`,
  ],
  MX: [
    `${PAYMENT_ICON_BASE}/visa.png`,
    `${PAYMENT_ICON_BASE}/mastercard.png`,
    `${PAYMENT_ICON_BASE}/paypal.png`,
  ],
  ROW: [
    `${PAYMENT_ICON_BASE}/visa.png`,
    `${PAYMENT_ICON_BASE}/mastercard.png`,
    `${PAYMENT_ICON_BASE}/paypal.png`,
  ],
};

export const RingProBottomBar = ({ className }: { className?: string }) => {
    const t = useTranslations('ring');
    const tCommon = useTranslations('common');
    const RingProSelectionClient = useContext(RingProSelectionContext);
    const nextStepLabel = useNextStepLabel(RingProSelectionClient.activeStepId);
    const CartClient = useCartStore(
      useShallow((s) => ({ store: s.store, taxString: s.taxString })),
    );
    const ModalClient = useModal();
    const { resetCounter: resetRetouchCounter } = useRetouchFlowContext();
    const formatPriceWithComma = useFormatPrice();

    const {
      size,
      canAddToCart,
      shippingEstimate,
      color,
      chargerCaseColor,
      defaultCurrency: currency,
      engraving,
      notServicable,
      goToNextStep,
      isLoading,
      tradeInDevice,
      tradeInCredit,
      maxCart,
    } = RingProSelectionClient;

    const handleCheckout = () => {
      if (RingProSelectionClient.size) {
        RingProSelectionClient.size
          ? analytics.track(`Bottom bar addto cart - CLICK`)
          : analytics.track(`Bottom bar select color-size - CLICK`);
      }
      openCheckoutInfo();
    };

    const openCheckoutInfo = () => {
      handleProceedClick();
      return;
    };

    const handleProceedClick = () => {
      if (isLoading) return;
      analytics.track('ring-pro-checkout', {
        clicked: true,
      });
      resetRetouchCounter(); // Reset retouch flow counter on Add to Cart
      RingProSelectionClient.checkout();
      ModalClient.hide();
    };

    const sctollToOrderSummary = () => {
      const ref = document.getElementById('order-summary-card');
      if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const { store } = CartClient;

    const canExpressCheckout = canAddToCart && !!color && !!chargerCaseColor;

    const paymentIcons = store
      ? (PAYMENT_ICONS_BY_STORE[store] ?? DEFAULT_PAYMENT_ICONS)
      : DEFAULT_PAYMENT_ICONS;

    const PaymentMethodsIcon = (
      <div className={styles.offerIcons}>
        {paymentIcons.map((src, i) => (
          <Image
            key={i}
            src={src}
            alt=""
            height="24"
            width="24"
            className={styles.offerIcon}
          />
        ))}
      </div>
    );

    const MidPriceSection = (
      <>
        <MidPanel className={styles.midPanel}>
          {PaymentMethodsIcon}
          <div>
            <p className={styles.totalPriceSingle}>
              {RingProSelectionClient.currencySymbol}
              {formatPriceWithComma(RingProSelectionClient.effectivePrice)}
              <span className={styles.taxDisclaimer}>({CartClient.taxString})</span>
            </p>
            {tradeInDevice && tradeInCredit > 0 && (
              <p className={styles.tradeInCredit}>
                -{RingProSelectionClient.currencySymbol}
                {formatPriceWithComma(tradeInCredit)}{' '}
                {tCommon('bottomBar.text.tradeCredits')}
              </p>
            )}
          </div>
        </MidPanel>

        <MidPanelPhone className={styles.midPanelPhone} onClick={sctollToOrderSummary}>
          <div className={styles.midPanelPhoneRow}>
            <div className={styles.subtitleTextBtn}>
              {tCommon('bottomBar.content.total')}
            </div>
            <div className={styles.midPanelPhoneRight}>
              {PaymentMethodsIcon}
              <div className={styles.midPanelPhonePrice}>
                <p className={styles.totalPriceSingle}>
                  {RingProSelectionClient.currencySymbol}
                  {formatPriceWithComma(RingProSelectionClient.effectivePrice)}
                  <span className={styles.taxDisclaimer}>
                    ({CartClient.taxString})
                  </span>
                </p>
                {tradeInDevice && tradeInCredit > 0 && (
                  <p className={styles.tradeInCredit}>
                    -{RingProSelectionClient.currencySymbol}
                    {formatPriceWithComma(tradeInCredit)}{' '}
                    {tCommon('bottomBar.text.tradeCredits')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </MidPanelPhone>
      </>
    );

    if (notServicable === true) return null;

    return (
      <div className={cx(styles.root, 'u-grid', className)} data-bottom-bar>
        <LeftPanel>
          {shippingEstimate ? (
            <p className={styles.chatSpecialist}>
              <ShippingBox /> {shippingEstimate}
            </p>
          ) : null}
        </LeftPanel>

        {RingProSelectionClient.size ? MidPriceSection : null}

        <RightPanel>
          <div className={styles.ctaStack}>
            {hasMaxSelected(maxCart) ? null : (
              <ApplePayExpressButton
                totalAmount={RingProSelectionClient.effectivePrice ?? 0}
                getLineItems={
                  RingProSelectionClient.getExpressCheckoutLineItems
                }
                ringColor={RingProSelectionClient.color ?? undefined}
                product="Ultrahuman Ring PRO"
                onCheckoutBlocked={
                  canExpressCheckout ? undefined : goToNextStep
                }
              />
            )}
            <PrimaryButton
              onClick={canAddToCart ? handleCheckout : goToNextStep}
              disabled={canAddToCart && isLoading}
              aria-busy={canAddToCart && isLoading}
              data-buttontype={canAddToCart ? 'add to cart' : 'go to next step'}
              data-variantId={
                color && size && currency
                  ? RingProVariants[color]?.[size]?.[getEngravingKey(engraving)]
                      ?.variantId[store as ShopifyStore]
                  : ''
              }
              data-product="Ultrahuman Ring PRO"
            >
              {canAddToCart && isLoading ? (
                <DotSpinner color="#000000" />
              ) : canAddToCart ? (
                t('ring.productSelection.cartSummary.addToCart')
              ) : (
                nextStepLabel
              )}
            </PrimaryButton>
          </div>
        </RightPanel>
      </div>
    );
};
